export const LPG_PRODUCT_ID = '4588b5e6-7309-49d0-9107-59cbb96b6bd8';
export const GASOLINE_PRODUCT_ID = '4588b5e6-7309-49d0-9107-59cbb96b6bd7';

export const LPG_CARGO_RATIO_PRODUCT_TYPE_IDS = [
  '4588b5e6-7309-49d0-9107-59cbb96b6b18',
  '4588b5e6-7309-49d0-9107-59cbb96b6b19',
];

export const isLpgProductId = productNameId => productNameId === LPG_PRODUCT_ID;

export const isGasolineProductId = productNameId =>
  productNameId === GASOLINE_PRODUCT_ID;

// Non-LPG products that show a single alpha column titled "Alpha"
// (instead of Alpha Dyed / Alpha Undyed). Matched against the product
// name with exact, case-insensitive comparison.
export const SINGLE_ALPHA_PRODUCT_NAMES = ['avtur', 'high speed diesel'];

export const isSingleAlphaProductName = productName => {
  const name = String(productName ?? '')
    .trim()
    .toLowerCase();
  return SINGLE_ALPHA_PRODUCT_NAMES.includes(name);
};

export const usesDyedUndyedAlpha = productNameId =>
  isGasolineProductId(productNameId);

// Gasoline parcels (quantity delivery terms) ask for Dyed, Undyed or both via
// isDyed / isUndyed. Parcels saved before that choice have no flags and show
// both. Other products use a single alpha column, so both are false.
export const resolveDyedUndyedColumns = (usesDyedUndyed, parcel) => {
  if (!usesDyedUndyed) {
    return { dyed: false, undyed: false };
  }
  return {
    dyed: (parcel?.isDyed ?? parcel?.IsDyed) !== false,
    undyed: (parcel?.isUndyed ?? parcel?.IsUndyed) !== false,
  };
};

export const getDyedUndyedColumns = (productNameId, parcel) =>
  resolveDyedUndyedColumns(usesDyedUndyedAlpha(productNameId), parcel);

// Number of alpha columns a row shows: 1 for a single alpha, else one per
// selected variant.
export const countAlphaColumns = ({ dyed, undyed }) =>
  dyed || undyed ? Number(dyed) + Number(undyed) : 1;

// "Dyed", "Undyed" or "Dyed & Undyed" for a gasoline parcel.
export const getDyedUndyedLabel = parcel => {
  const { dyed, undyed } = getDyedUndyedColumns(GASOLINE_PRODUCT_ID, parcel);
  if (dyed && undyed) return 'Dyed & Undyed';
  return dyed ? 'Dyed' : 'Undyed';
};

export const getAlphaColumnLabel = pricingUnit =>
  pricingUnit ? `Alpha (${pricingUnit})` : 'Alpha';

export const getProcurementProductNameId = procurement =>
  procurement?.productType?.product?.id ??
  procurement?.productType?.product?.Id ??
  procurement?.productType?.Product?.id ??
  procurement?.productType?.Product?.Id ??
  null;

export const getProcurementProductTypeId = procurement =>
  procurement?.productType?.id ?? procurement?.productType?.Id ?? null;

export const isLpgProcurement = procurement =>
  isLpgProductId(getProcurementProductNameId(procurement));

export const isLpgProduct = isLpgProcurement;

export const requiresLpgCargoRatio = form => {
  if (!isLpgProductId(form?.productNameId)) return false;
  const productTypeId =
    form?.productTypeId ?? form?.productType?.id ?? form?.productType?.Id;
  return LPG_CARGO_RATIO_PRODUCT_TYPE_IDS.includes(String(productTypeId));
};

export const requiresLpgCargoRatioSelection = procurement =>
  requiresLpgCargoRatio({
    productNameId: getProcurementProductNameId(procurement),
    productTypeId: getProcurementProductTypeId(procurement),
  });

// Procurements may hold several LPG product types (GET /procurement/{id}
// returns them as procurementProductTypes, primary first) and each cargo
// (quantity delivery term) carries its own productTypeId. Cargoes saved before
// that have no productTypeId and fall back to the procurement's product type.

const readProductTypeEntry = item => {
  if (!item || typeof item !== 'object') return null;
  const nested = item.productType ?? item.ProductType;
  const productTypeId =
    item.productTypeId ??
    item.ProductTypeId ??
    nested?.id ??
    nested?.Id ??
    null;
  const productTypeName =
    item.productTypeName ??
    item.ProductTypeName ??
    nested?.name ??
    nested?.Name ??
    nested?.description ??
    nested?.Description ??
    null;
  if (!productTypeId && !productTypeName) return null;
  return { ...item, productTypeId, productTypeName };
};

export const getProcurementProductTypes = procurement => {
  const items =
    procurement?.procurementProductTypes ??
    procurement?.ProcurementProductTypes ??
    [];
  if (!Array.isArray(items)) return [];
  return items.map(readProductTypeEntry).filter(Boolean);
};

export const hasMultipleProductTypes = procurement =>
  new Set(
    getProcurementProductTypes(procurement).map(
      entry => entry.productTypeId ?? entry.ProductTypeId
    )
  ).size > 1;

const joinNames = names => {
  if (!names.length) return null;
  return names.join(', ');
};

// "A", "A, B", "A, B, C"; falls back to the single product type name.
export const getProcurementProductTypeNames = procurement => {
  const names = getProcurementProductTypes(procurement)
    .map(entry => entry.productTypeName ?? entry.ProductTypeName)
    .filter(Boolean);
  return (
    joinNames(names) ??
    procurement?.productType?.name ??
    procurement?.productType?.Name ??
    null
  );
};

export const getCargoProductTypeId = (procurement, cargo) =>
  cargo?.productTypeId ??
  cargo?.ProductTypeId ??
  getProcurementProductTypeId(procurement) ??
  procurement?.productTypeId ??
  null;

export const getCargoProductTypeName = (procurement, cargo) => {
  const ownName = cargo?.productTypeName ?? cargo?.ProductTypeName;
  if (ownName) return ownName;
  const productTypeId = getCargoProductTypeId(procurement, cargo);
  const entry = getProcurementProductTypes(procurement).find(
    item => (item.productTypeId ?? item.ProductTypeId) === productTypeId
  );
  return (
    entry?.productTypeName ??
    entry?.ProductTypeName ??
    procurement?.productType?.name ??
    null
  );
};

// Cargo ratio selection depends on the product type of the cargo, not the
// procurement's primary one.
export const requiresLpgCargoRatioForCargo = (procurement, cargo) =>
  requiresLpgCargoRatio({
    productNameId:
      getProcurementProductNameId(procurement) ?? procurement?.productNameId,
    productTypeId: getCargoProductTypeId(procurement, cargo),
  });

// List endpoints return productTypeNames (every product type, primary first).
// With more than one, the list shows all of them; otherwise the usual label.
export const getListProductTypeLabel = (row, fallback) => {
  const names = (row?.productTypeNames ?? row?.ProductTypeNames ?? []).filter(
    Boolean
  );
  if (names.length > 1) return joinNames(names);
  const entryNames = getProcurementProductTypes(row)
    .map(entry => entry.productTypeName)
    .filter(Boolean);
  return entryNames.length > 1 ? joinNames(entryNames) : fallback;
};
